import React, { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useSignupWizard, clearSignupSession } from './signup-wizard-context';
import WizardLayout from './wizard-layout';
import StepHeader from './step-header';
import BtButton from '../bt-button/bt-button';
import { ServiceSupplierSignup } from '../../services/service-supplier-signup';
import appState from '../../state/AppStateContainer';
import { ReactComponent as InfoIcon } from '../../assets/svg/info.svg';
import styles from './step-account-linked.module.scss';

const SUPPORT_EMAIL = 'support@accurator.se';
const supportLinkStyle: React.CSSProperties = { color: 'var(--orange-500)', textDecoration: 'underline' };

/**
 * Step 6b — shown when the Step 4 email already exists in Aceve.
 * No verification code is involved: clicking "Go to login" creates the
 * organization and links the existing Aceve user (server-side), then sends
 * the user to the login page to sign in with their existing credentials.
 */
const StepAccountLinked: FC = () => {
  const { t } = useTranslation('newSupplier', { keyPrefix: 'wizard' });
  const history = useHistory();
  const { state } = useSignupWizard();
  const { email } = state;

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const handleGoToLogin = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(false);
    try {
      const { orgSlug } = await ServiceSupplierSignup.verifyAndCreate({
        organizationNumber: state.organizationNumber,
        companyName: state.companyName,
        address: state.address,
        city: state.city,
        postalCode: state.postalCode,
        termsAcceptedAt: state.termsAcceptedAt as string,
        areaIds: state.areaIds,
        places: state.places,
        name: state.name,
        title: state.title,
        phoneNumber: state.phoneNumber,
        email: state.email,
        addAsContact: state.addAsContact,
        colleagues: state.colleagues,
        sessionId: state.sessionId,
      });
      clearSignupSession();
      // Land on the newly created org's supplier presentation after the user
      // signs in. The login flow (app-login setTokenAndRedirect) consumes this
      // once it confirms the user has access to the org in the path.
      if (orgSlug) appState.persistString('urlToRedirect', `/${orgSlug}/supplier-presentation`);
      window.location.href = '/';
    } catch {
      setError(true);
      setSubmitting(false);
    }
  };

  return (
    <WizardLayout>
      <div className="flex flex-col gap-6">
        <StepHeader
          title={t('accountLinked.title', 'Du har redan ett konto')}
          stepNumber={6}
          description={t('accountLinked.description', 'Konto hittat')}
        />

        <div className={styles.linkedCard}>
          <div className={styles.linkedHead}>
            <span className={styles.linkedIcon}><InfoIcon width={22} height={22} /></span>
            <div>
              <p className={styles.linkedTitle}>
                {t('accountLinked.cardTitle', 'Den här e-postadressen finns redan i Aceve')}
              </p>
              <div className={styles.linkedText}>
                <Trans
                  t={t}
                  i18nKey="accountLinked.cardBody"
                  values={{ email }}
                  components={[<strong key="email" />]}
                  defaults="Ett konto med <0>{{email}}</0> finns redan i Aceve. När du klickar på knappen skapa kommer det att länkas till Accurator."
                />
              </div>
            </div>
          </div>
          <div className={styles.linkedCreds}>
            {t('accountLinked.creds', 'Du kan logga in på Accurator med samma uppgifter som du redan använder för ditt Aceve-konto.')}
          </div>
        </div>

        {error && (
          <p style={{ fontSize: '0.875rem', color: 'var(--red-700)' }}>
            {t('accountLinked.error', 'Något gick fel. Försök igen eller')}{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} style={supportLinkStyle}>
              {t('accountLinked.contactSupport', 'kontakta support')}
            </a>
          </p>
        )}

        <div className="flex items-center justify-between">
          <BtButton color="white" onClick={() => history.push('/supplier-sign-up/invite')}>
            {t('layout.back', 'Tillbaka')}
          </BtButton>
          <BtButton onClick={handleGoToLogin} loaderShow={submitting} disabled={submitting}>
            {t('accountLinked.goToLogin', 'Skapa och gå till inloggning')}
          </BtButton>
        </div>
      </div>
    </WizardLayout>
  );
};

export default StepAccountLinked;
